import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  formatLoaderSerial,
  getHolidayName,
  isKoreanHoliday,
  LOADER_INSPECTION_LABELS,
  LOADER_PRODUCTION_KIND_LABELS,
  LOADER_STATUS_LABELS,
  LoaderInspection,
  LoaderProductionKind,
  LoaderStatus,
  UserRole,
} from '@bhops/shared';
import { loaderRevsApi, loadersApi, type LoaderSummary } from '@/api/loaders';
import { extractApiError } from '@/api/client';
import { Button, Field, Input, Textarea, FormError } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Icon } from '@/components/Icon';
import { LoaderConfigCode } from '@/components/LoaderConfigCode';
import { kstYmd } from '@/lib/format-date';
import { canEditMfg } from '@/permissions';
import { useAuthStore } from '@/stores/auth';
import { EditLoaderModal, LoaderRevPicker, toLoaderRevOption } from './EditLoaderModal';

// docs/plan/loader-history-traceability-2026-07 — 로더 시리얼 마스터 목록.
//   로더(로봇 팔) 한 대 한 대의 이동·개조·불량을 추적하는 진입 목록.
//   - 시리얼 검색(on-submit) + 상태 필터 + 삭제 포함 토글.
//   - manager+ 는 인라인 '로더 등록' 카드로 신규 등록(빈 시리얼 → 자동 채번).
//     추가수정 2차(26-07-27) §3 — 입고일만 고르면 생산연월·시리얼이 자동으로 따라오고,
//     업체는 '구분'(사내/외주) 하나로 갈음한다(외주 → 서버가 H5 자동 기록).
//   - 행 클릭 → 로더 상세(/manufacturing/loaders/:id).
// 권한: 페이지 진입은 제조 그룹 공통, mutation 은 canEditMfg 로 게이트(백엔드도 가드).
//
// 개선 9건 차수1(26-08-04) — 로더 대장을 콘솔이 정본으로 삼기 위한 3가지:
//   - 입고검사 판정 필터(새 값 '조건부합격'을 걸러 볼 수 있게).
//   - 행에서 삭제(제조팀) · 복구/완전삭제(ADMIN). 서버에 있던 소프트 삭제가
//     화면에 버튼이 없어 못 쓰이고 있었다.

export function LoadersListPage() {
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = !!role && canEditMfg(role);
  // 개선 9건 차수1 — 복구·완전삭제는 ADMIN 전용. 서버(loaders.service.assertAdmin)와
  // **정확히 같은 조건**이어야 한다 — 넓히면 버튼만 열리고 저장만 403 난다.
  const canAdmin = role === UserRole.ADMIN;

  const [searchDraft, setSearchDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [status, setStatus] = useState<LoaderStatus | ''>('');
  const [inspection, setInspection] = useState<LoaderInspection | ''>('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  // 행 편집 아이콘 — 대상 로더 id.
  const [editId, setEditId] = useState<string | null>(null);
  // 행 작업 확인창 — 무엇을 어느 로더에 하려는지.
  const [pending, setPending] = useState<{ kind: RowAction; loader: LoaderSummary } | null>(null);

  const listQ = useQuery({
    queryKey: ['loaders', { q: appliedSearch, status, inspection, active: includeDeleted }],
    queryFn: () =>
      loadersApi.list({
        q: appliedSearch || undefined,
        status: status || undefined,
        inspection: inspection || undefined,
        active: includeDeleted ? 'all' : undefined,
      }),
    staleTime: 30_000,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['loaders'] });
  }

  const actionM = useMutation({
    mutationFn: async () => {
      if (!pending) return;
      if (pending.kind === 'delete') await loadersApi.remove(pending.loader.id);
      else if (pending.kind === 'restore') await loadersApi.restore(pending.loader.id);
      else await loadersApi.hardDelete(pending.loader.id);
    },
    onSuccess: () => {
      invalidate();
      setPending(null);
    },
  });

  function onSubmitSearch(e: FormEvent) {
    e.preventDefault();
    setAppliedSearch(searchDraft.trim());
  }

  const items = listQ.data?.items ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={headerRow}>
        <div style={{ minWidth: 0 }}>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            로더 이력
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <Link to="/manufacturing/loaders/quality" style={pillLink}>
            <Icon name="factory" size={14} aria-hidden />
            업체 품질
          </Link>
          <Link to="/manufacturing/loader-revs" style={pillLink}>
            <Icon name="wrench" size={14} aria-hidden />
            개조 관리
          </Link>
        </div>
      </header>

      {/* 검색 + 필터 */}
      <form onSubmit={onSubmitSearch} style={searchBar}>
        <Input
          placeholder="시리얼 검색 (예: G2H2607)"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          // 시리얼은 9자 안팎 — 줄 끝까지 늘이지 않는다(2026-11-03 오너 "검색창이 쓸데없이 너무 길다").
          style={{ flex: '0 1 260px', minWidth: 180 }}
          aria-label="로더 시리얼 검색"
        />
        <Button type="submit">검색</Button>
        {appliedSearch && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setSearchDraft('');
              setAppliedSearch('');
            }}
          >
            초기화
          </Button>
        )}
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as LoaderStatus | '')}
          style={selectStyle}
          aria-label="상태 필터"
        >
          <option value="">전체</option>
          <option value={LoaderStatus.IN_STOCK}>
            {LOADER_STATUS_LABELS[LoaderStatus.IN_STOCK]}
          </option>
          <option value={LoaderStatus.INSTALLED}>
            {LOADER_STATUS_LABELS[LoaderStatus.INSTALLED]}
          </option>
          <option value={LoaderStatus.IN_REPAIR}>
            {LOADER_STATUS_LABELS[LoaderStatus.IN_REPAIR]}
          </option>
          <option value={LoaderStatus.RETIRED}>{LOADER_STATUS_LABELS[LoaderStatus.RETIRED]}</option>
        </select>
        {/* 개선 9건 차수1(26-08-04) — 입고검사 판정 필터. 선택지는 라벨 맵 순회로
            만든다(등록·편집 창과 같은 방식) — 값이 늘면 여기도 자동으로 따라온다. */}
        <select
          value={inspection}
          onChange={(e) => setInspection(e.target.value as LoaderInspection | '')}
          style={selectStyle}
          aria-label="입고검사 필터"
        >
          <option value="">입고검사 전체</option>
          {Object.entries(LOADER_INSPECTION_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
        <label style={inlineCheck}>
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          삭제 포함
        </label>
      </form>

      {/* 신규 등록 */}
      {canEdit && <CreateLoaderForm onCreated={invalidate} />}

      {/* 리스트 */}
      <section style={card}>
        <div style={listHeader}>
          <strong style={{ fontSize: 13 }}>로더 ({items.length})</strong>
        </div>
        {listQ.isLoading ? (
          <div style={emptyRow}>불러오는 중…</div>
        ) : listQ.error ? (
          <FormError>{extractApiError(listQ.error)}</FormError>
        ) : items.length === 0 ? (
          <div style={emptyRow}>
            {canEdit ? '로더가 없습니다. 위에서 등록해 주세요.' : '로더가 없습니다.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableBase}>
              <thead>
                <tr>
                  <th style={th}>시리얼</th>
                  <th style={th}>구분</th>
                  <th style={th}>입고일</th>
                  <th style={th}>현재 로봇</th>
                  {/* 개선 9건 차수1 — 입고검사 필터를 달았으니 값도 보여야 한다
                      (걸러 놓고 왜 걸러졌는지 화면에서 확인이 안 되면 안 된다). */}
                  <th style={th}>입고검사</th>
                  <th style={th}>구성코드</th>
                  <th style={{ ...th, textAlign: 'right' }}>개조</th>
                  <th style={th}>상태</th>
                  {canEdit && <th style={{ ...th, textAlign: 'right' }}>작업</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <LoaderRow
                    key={l.id}
                    loader={l}
                    canEdit={canEdit}
                    canAdmin={canAdmin}
                    onEdit={() => setEditId(l.id)}
                    onAction={(kind) => setPending({ kind, loader: l })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editId && <EditLoaderModal loaderId={editId} onClose={() => setEditId(null)} />}

      {/* 개선 9건 차수1 — 삭제·복구·완전삭제 확인창. 무엇이 어떻게 되는지(되돌릴 수
          있는지)를 그 자리에서 말한다. 서버가 거부하면 그 문구를 그대로 띄운다. */}
      {pending && (
        <ConfirmDialog
          open
          title={ROW_ACTION_TITLES[pending.kind]}
          message={rowActionMessage(pending.kind, pending.loader)}
          confirmLabel={ROW_ACTION_CONFIRM_LABELS[pending.kind]}
          danger={pending.kind !== 'restore'}
          busy={actionM.isPending}
          onConfirm={async () => {
            try {
              await actionM.mutateAsync();
            } catch (err) {
              // ConfirmDialog 가 Error.message 를 인라인으로 띄운다 — 서버의
              // 한글 거부 사유(장착 중 · 이력 있음)를 그대로 보여주기 위한 변환.
              throw new Error(extractApiError(err));
            }
          }}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// 행 작업 (개선 9건 차수1)
// ============================================================

type RowAction = 'delete' | 'restore' | 'purge';

const ROW_ACTION_TITLES: Record<RowAction, string> = {
  delete: '로더 삭제',
  restore: '로더 복구',
  purge: '로더 완전삭제',
};

const ROW_ACTION_CONFIRM_LABELS: Record<RowAction, string> = {
  delete: '삭제',
  restore: '복구',
  purge: '완전삭제',
};

function rowActionMessage(kind: RowAction, l: LoaderSummary): string {
  const head = `${l.serial}`;
  if (kind === 'delete') {
    return [
      `${head} 를 삭제 목록으로 옮깁니다.`,
      '',
      '• 장착 이력과 개조 기록은 그대로 남습니다.',
      '• 목록의 「삭제 포함」을 켜면 다시 보이고, 관리자가 복구할 수 있습니다.',
      '• 로봇에 장착 중인 로더는 지울 수 없습니다. 먼저 교체하거나 떼어 내 주세요.',
      '',
      '계속하시겠습니까?',
    ].join('\n');
  }
  if (kind === 'restore') {
    return [`${head} 를 삭제 목록에서 되돌립니다.`, '', '계속하시겠습니까?'].join('\n');
  }
  return [
    `${head} 를 데이터베이스에서 완전히 지웁니다.`,
    '',
    '• 되돌릴 수 없습니다.',
    '• 장착 이력이나 개조 이력이 하나라도 있으면 지울 수 없습니다.',
    '• 잘못 등록한 시리얼을 정리할 때만 써 주세요.',
    '',
    '계속하시겠습니까?',
  ].join('\n');
}

// ============================================================
// 로더 한 행
// ============================================================

function LoaderRow({
  loader: l,
  canEdit,
  canAdmin,
  onEdit,
  onAction,
}: {
  loader: LoaderSummary;
  canEdit: boolean;
  canAdmin: boolean;
  onEdit: () => void;
  onAction: (kind: RowAction) => void;
}) {
  const deleted = !!l.deletedAt;
  return (
    <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
      <td style={td}>
        <Link
          to={`/manufacturing/loaders/${l.id}`}
          style={{
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            color: 'var(--fg-brand)',
            // 개선 9건 차수1 — 삭제된 행임을 한눈에(「삭제 포함」을 켰을 때만 나온다).
            textDecoration: deleted ? 'line-through' : undefined,
          }}
        >
          {l.serial}
        </Link>
        {deleted && <span style={deletedBadge}>삭제됨</span>}
        <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{formatLoaderSerial(l.serial)}</div>
      </td>
      <td style={td}>{LOADER_PRODUCTION_KIND_LABELS[l.productionKind]}</td>
      {/* 표시 날짜는 KST 정본 한 벌(상세 화면과 같은 규칙·같은 표기). */}
      <td style={td}>{l.receivedAt ? (kstYmd(l.receivedAt) ?? '-') : '–'}</td>
      <td style={td}>
        {l.currentRobotSerial ? (
          <Link
            to={`/robots/${l.currentRobotId}`}
            style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-brand)' }}
          >
            #{l.currentRobotSerial}
          </Link>
        ) : (
          <span style={{ color: 'var(--fg-muted)' }}>–</span>
        )}
      </td>
      <td style={td}>{LOADER_INSPECTION_LABELS[l.inspectionResult]}</td>
      {/* 구성코드 — 코드만으로는 무슨 개조인지 알 수 없어 개조 관리 화면을 따로 열어
          대조해야 했다. 올리면 코드별 이름이 뜬다(목록이라 탭 정지점은 만들지 않는다). */}
      <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
        <LoaderConfigCode
          code={l.configCode}
          fallback={<span style={{ color: 'var(--fg-muted)', fontFamily: 'inherit' }}>–</span>}
        />
      </td>
      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {l.revCount}
      </td>
      <td style={td}>
        <span style={statusPill}>{LOADER_STATUS_LABELS[l.status]}</span>
      </td>
      {canEdit && (
        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {deleted ? (
            // 삭제된 행에는 편집·삭제가 아니라 복구/완전삭제만 — 둘 다 ADMIN.
            canAdmin ? (
              <>
                <button
                  type="button"
                  onClick={() => onAction('restore')}
                  style={rowTextBtn}
                  aria-label={`${l.serial} 복구`}
                >
                  복구
                </button>
                <button
                  type="button"
                  onClick={() => onAction('purge')}
                  style={{ ...rowTextBtn, color: 'var(--fg-danger)' }}
                  aria-label={`${l.serial} 완전삭제`}
                >
                  완전삭제
                </button>
              </>
            ) : (
              <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>관리자만 복구</span>
            )
          ) : (
            <>
              <button
                type="button"
                onClick={onEdit}
                style={rowEditBtn}
                aria-label={`${l.serial} 편집`}
                title="로더 편집"
              >
                <Icon name="pen" size={14} aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => onAction('delete')}
                style={{ ...rowEditBtn, color: 'var(--fg-danger)' }}
                aria-label={`${l.serial} 삭제`}
                title="로더 삭제"
              >
                <Icon name="trash" size={14} aria-hidden />
              </button>
            </>
          )}
        </td>
      )}
    </tr>
  );
}

// ============================================================
// 신규 등록 폼
// ============================================================

// 추가수정 2차(26-07-27) §3 — 입고일(yyyy-mm-dd) → 생산연월(YYMM) 파생.
//   ⚠ Date 객체를 거치지 않고 문자열에서 잘라낸다. 입고일은 `T00:00:00.000Z` 로
//   전송되므로 new Date(...).getMonth() 를 쓰면 KST(+9) 에서 하루/한 달이 밀린다.
function productionYmFromYmd(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(ymd.trim());
  return m ? m[1].slice(2) + m[2] : '';
}

function CreateLoaderForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [serial, setSerial] = useState('');
  const [productionKind, setProductionKind] = useState<LoaderProductionKind>(
    LoaderProductionKind.IN_HOUSE,
  );
  const [productionYm, setProductionYm] = useState('');
  // 추가수정 2차(26-07-27) §3 — 생산연월을 손으로 고치면 그 뒤로는 입고일이
  // 바뀌어도 자동 갱신하지 않는다(로봇 등록 창의 Short SN dirty 패턴과 동일).
  // 6월 생산 → 7월 입고 같은 이월 입고분에 틀린 시리얼이 발급되지 않게 하기 위함.
  const [productionYmDirty, setProductionYmDirty] = useState(false);
  const [receivedAt, setReceivedAt] = useState('');
  const [inspectionResult, setInspectionResult] = useState<LoaderInspection>(
    LoaderInspection.PENDING,
  );
  const [notes, setNotes] = useState('');
  // null = 아직 손대지 않음 → 자동 적용 개조가 미리 체크된 상태로 보인다(결정 ①A).
  // 담당자가 하나라도 토글하면 그 순간의 선택이 그대로 굳는다.
  const [revIdsDraft, setRevIdsDraft] = useState<string[] | null>(null);

  const revsQ = useQuery({
    queryKey: ['loader-revs', 'picker'],
    queryFn: () => loaderRevsApi.list(),
    enabled: open,
    staleTime: 60_000,
  });
  const revOptions = useMemo(() => (revsQ.data?.items ?? []).map(toLoaderRevOption), [revsQ.data]);
  const selectedRevIds =
    revIdsDraft ?? revOptions.filter((r) => r.autoApplyToNewLoaders).map((r) => r.id);

  function toggleRev(revId: string, checked: boolean): void {
    setRevIdsDraft(
      checked ? [...selectedRevIds, revId] : selectedRevIds.filter((cur: string) => cur !== revId),
    );
  }

  // 추가수정 2차(26-07-27) §3 — 입고일을 고르면 생산연월이 따라오고, 그 즉시
  // 기존 next-serial 미리보기가 뜬다. 손대기 전까지는 입고일을 지우면 같이 지운다.
  function onReceivedAtChange(next: string): void {
    setReceivedAt(next);
    if (productionYmDirty) return;
    setProductionYm(productionYmFromYmd(next));
  }

  // 시리얼 자동 채번 미리보기 — 시리얼이 비어 있고 생산연월(YYMM)이 있을 때만.
  const ymTrimmed = productionYm.trim();
  const nextSerialQ = useQuery({
    queryKey: ['loaders', 'next-serial', ymTrimmed, productionKind],
    queryFn: () => loadersApi.nextSerial(ymTrimmed, productionKind),
    enabled: open && serial.trim().length === 0 && ymTrimmed.length > 0,
    staleTime: 30_000,
  });

  const createM = useMutation({
    mutationFn: () =>
      // 추가수정 2차(26-07-27) §3 (결정 ④A) — vendor 는 보내지 않는다.
      // 구분=외주면 서버가 H5 를 자동 저장한다(loaders.service.ts create).
      loadersApi.create({
        serial: serial.trim() || undefined,
        productionKind,
        productionYm: ymTrimmed || undefined,
        receivedAt: receivedAt ? `${receivedAt}T00:00:00.000Z` : undefined,
        inspectionResult,
        notes: notes.trim() || undefined,
        revIds: selectedRevIds.length ? selectedRevIds : undefined,
      }),
    onSuccess: () => {
      onCreated();
      setSerial('');
      setProductionYm('');
      setProductionYmDirty(false);
      setReceivedAt('');
      setNotes('');
      setProductionKind(LoaderProductionKind.IN_HOUSE);
      setInspectionResult(LoaderInspection.PENDING);
      setRevIdsDraft(null);
      setOpen(false);
    },
  });

  if (!open) {
    return (
      <div>
        <Button type="button" onClick={() => setOpen(true)}>
          + 로더 등록
        </Button>
      </div>
    );
  }

  return (
    <div style={addCard}>
      <strong style={{ fontSize: 13 }}>로더 등록</strong>
      <div style={{ marginTop: 8 }}>
        <div style={formGrid}>
          <Field
            label="시리얼 (선택)"
            htmlFor="loader-serial"
            hint="빈칸이면 생산연월 기준으로 자동 채번됩니다."
          >
            <Input
              id="loader-serial"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="G2H260701"
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>
          <Field label="구분" htmlFor="loader-kind">
            <select
              id="loader-kind"
              value={productionKind}
              onChange={(e) => setProductionKind(e.target.value as LoaderProductionKind)}
              style={selectStyle}
            >
              <option value={LoaderProductionKind.IN_HOUSE}>
                {LOADER_PRODUCTION_KIND_LABELS[LoaderProductionKind.IN_HOUSE]}
              </option>
              <option value={LoaderProductionKind.OUTSOURCED}>
                {LOADER_PRODUCTION_KIND_LABELS[LoaderProductionKind.OUTSOURCED]}
              </option>
            </select>
          </Field>
          {/* 추가수정 2차(26-07-27) §3 — 입고일이 생산연월을 채우므로 앞에 둔다.
              추가수정 2차(26-07-27) 정정 — 공휴일 경고(결정 ③A)를 이 칸에도.
              §3 로 등록 동선의 중심이 된 칸인데 확산에서 빠져 있었다. 방식은
              InfrastructureOrderModal / DeployRobotModal 과 동일. */}
          <Field
            label="입고일 (선택)"
            htmlFor="loader-received"
            hint={
              isKoreanHoliday(receivedAt)
                ? `공휴일 (${getHolidayName(receivedAt)}) 입니다`
                : undefined
            }
          >
            <Input
              id="loader-received"
              type="date"
              value={receivedAt}
              onChange={(e) => onReceivedAtChange(e.target.value)}
            />
          </Field>
          <Field
            label="생산연월 (선택)"
            htmlFor="loader-ym"
            hint="YYMM 형식입니다. 입고일에서 자동으로 채워지며, 이월 입고분은 직접 고쳐 주세요. 예: 2607"
          >
            <Input
              id="loader-ym"
              value={productionYm}
              onChange={(e) => {
                setProductionYm(e.target.value);
                setProductionYmDirty(true);
              }}
              placeholder="2607"
              inputMode="numeric"
            />
          </Field>
          <Field label="입고검사" htmlFor="loader-inspection">
            <select
              id="loader-inspection"
              value={inspectionResult}
              onChange={(e) => setInspectionResult(e.target.value as LoaderInspection)}
              style={selectStyle}
            >
              {/* 오너 결정(2026-08-03) '조건부합격' 추가 — 값을 하나하나 적던 것을
                  라벨 맵 순회로 바꿨다(편집 모달과 같은 방식). 이제 선택지와 그
                  순서는 LOADER_INSPECTION_LABELS 한 곳에서만 정해진다. */}
              {Object.entries(LOADER_INSPECTION_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {serial.trim().length === 0 && ymTrimmed.length > 0 && nextSerialQ.data && (
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
            다음 시리얼:{' '}
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-default)' }}>
              {nextSerialQ.data.serial}
            </span>
          </div>
        )}

        <Field
          label="적용 개조 (선택)"
          hint="자동 적용으로 지정된 개조는 미리 체크되어 있습니다. 필요하면 체크를 풀어 주세요."
        >
          <LoaderRevPicker
            idPrefix="create-loader"
            options={revOptions}
            selected={selectedRevIds}
            loading={revsQ.isLoading}
            error={revsQ.error}
            onToggle={toggleRev}
            showAutoBadge
          />
        </Field>

        <Field label="비고 (선택)" htmlFor="loader-notes">
          <Textarea
            id="loader-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="특이사항"
          />
        </Field>
      </div>

      {createM.isError && <FormError>{extractApiError(createM.error)}</FormError>}

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <Button type="button" onClick={() => createM.mutate()} disabled={createM.isPending}>
          {createM.isPending ? '저장 중…' : '저장'}
        </Button>
        <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
          취소
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Styles
// ============================================================

// 2026-11-03 오너 "너무 멀리있어" — 업체 품질 · 개조 관리를 화면 오른쪽 끝이 아니라 제목 바로 옆에 둔다.
const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
};

const pillLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  padding: '6px 12px',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const searchBar: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};

const selectStyle: CSSProperties = {
  padding: '8px 10px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 14,
};

const inlineCheck: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 12,
};

const addCard: CSSProperties = {
  ...card,
  background: 'var(--bg-sunken)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const formGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 8,
};

const listHeader: CSSProperties = {
  marginBottom: 4,
};

const tableBase: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-subtle)',
  whiteSpace: 'nowrap',
};

const td: CSSProperties = {
  fontSize: 13,
  padding: '8px 10px',
  verticalAlign: 'middle',
};

const statusPill: CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--bg-sunken)',
  border: '1px solid var(--border-default)',
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};

const rowEditBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--fg-muted)',
  padding: 4,
  lineHeight: 0,
  borderRadius: 'var(--r-btn)',
};

// 개선 9건 차수1(26-08-04) — 삭제 행의 복구/완전삭제는 아이콘이 뜻을 못 담아 글자로.
const rowTextBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--fg-brand)',
  fontSize: 12,
  padding: '2px 6px',
  borderRadius: 'var(--r-btn)',
};

const deletedBadge: CSSProperties = {
  marginLeft: 6,
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-sunken)',
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};

const emptyRow: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--fg-muted)',
};
